/* What a row on the incident queue means, decided once and in one place.
 *
 * The screen renders whatever the server sends, so almost nothing here is presentation. What is here
 * is the two judgements a component would otherwise make inline and get subtly wrong:
 *
 * * **Severity is not a colour.** `toneOf` maps it to a semantic tone, and the component maps the
 *   tone to a token — so a screen cannot decide that critical looks like warning today. The
 *   important part is that every tone also carries a *word*: WCAG 1.4.1, and this codebase has
 *   already shipped a colour-alone status once, on the classification table, where approved,
 *   proposed and stale all rendered "1 column" distinguished by hue.
 * * **Acknowledged is not resolved.** `isActionable` is what a button reads. Treating acknowledged
 *   as done would drop it off the queue while the condition still holds — the exact confusion the
 *   backend refuses to make.
 */

import type {
  Incident,
  IncidentState,
  NotificationAttempt,
  Severity,
} from '@/shared/api/generated/observability';
import type { BadgeTone } from '@/shared/ui/StatusBadge';

/* Which incident kinds have a *dataset* as their subject.
 *
 * `Incident.subject_id`'s own docstring states that the referent depends on the kind: a dataset for
 * the four monitor kinds and a **contract** for `contract_breach`. So a dataset-name lookup is
 * meaningful for four of the five, and running it for a contract breach reports "this dataset is not
 * in the catalogue" about something that was never a dataset — a false statement rather than a
 * missing one.
 *
 * **Here rather than in each screen that names a subject.** It was a private set in
 * `features/home/model/overview.ts`, and the notification feed needed the same answer — two copies
 * of a five-member vocabulary is the two-vocabularies failure this codebase records at several
 * sites, and the copy nobody edits is the one that silently misses the sixth kind.
 *
 * The parameter is the wire `string` rather than `IncidentKind` because one caller does not hold an
 * incident at all: the dashboard names an anomaly finding's dataset through the same path and passes
 * a monitor kind to say so. Widening the type is what lets that call site stay honest about what it
 * is doing instead of casting.
 */
const DATASET_SUBJECT_KINDS: ReadonlySet<string> = new Set([
  'schema_drift',
  'freshness_breach',
  'volume_anomaly',
  'assessment_overdue',
]);

export function subjectIsDataset(incidentKind: string): boolean {
  return DATASET_SUBJECT_KINDS.has(incidentKind);
}

/** What a kind's `subject_id` points at, as three answers rather than two.
 *
 * **The sixth kind arrived, exactly as the note above predicted.**
 * `rule_persistent_failure` carries a *rule binding*, and with only `subjectIsDataset` to ask, both
 * screens that name a subject fell into their contract branch and said "Contract — not a dataset"
 * about a rule. A false statement about a customer's data, which is worse than a missing one.
 *
 * The wire now carries `subject_kind` for precisely this, and a client should read it rather than
 * infer. This mapping stays for the callers that hold only a kind — the dashboard passes a monitor
 * kind through `subjectIsDataset` and holds no incident at all — and returns `'dataset'` for
 * anything it does not recognise, which is what that caller means by a monitor kind.
 */
export function subjectKindOf(
  incidentKind: string,
): 'dataset' | 'contract' | 'rule_binding' {
  if (incidentKind === 'contract_breach') return 'contract';
  if (incidentKind === 'rule_persistent_failure') return 'rule_binding';
  return 'dataset';
}

export type IncidentTone = 'critical' | 'warning' | 'info';

/* Severity to tone. A pass-through today, and named anyway.
 *
 * Two reasons it is not `incident.severity` at the call site. The set of severities is the server's
 * and the set of tones is the design system's, so a new severity is a decision here rather than an
 * unstyled badge; and the mapping is what a test can assert is total. */
const TONE_BY_SEVERITY: Readonly<Record<Severity, IncidentTone>> = {
  critical: 'critical',
  warning: 'warning',
  info: 'info',
};

export function toneOf(incident: Incident): IncidentTone {
  return TONE_BY_SEVERITY[incident.severity];
}

/** Whether a human can still act on this incident. Acknowledged counts: seen is not fixed. */
export function isActionable(incident: Incident): boolean {
  return incident.active;
}

/** Whether acknowledging is still available. Only an open incident can be acknowledged. */
export function canAcknowledge(incident: Incident): boolean {
  return incident.state === 'open';
}

/** Whether resolving is still available. Both active states may be resolved. */
export function canResolve(incident: Incident): boolean {
  return incident.active;
}

const SEVERITY_ORDER: Readonly<Record<Severity, number>> = {
  critical: 0,
  warning: 1,
  info: 2,
};

/* Most severe first, then oldest — the same order the API promises.
 *
 * Re-sorted client-side anyway, because the list is also re-rendered after an acknowledge that
 * changes a row in place, and a queue whose order depends on whether a mutation has landed is a
 * queue that appears to shuffle itself while somebody is reading it.
 */
export function orderForQueue(incidents: readonly Incident[]): readonly Incident[] {
  return [...incidents].sort((left, right) => {
    const bySeverity = SEVERITY_ORDER[left.severity] - SEVERITY_ORDER[right.severity];
    if (bySeverity !== 0) return bySeverity;
    return left.opened_at.localeCompare(right.opened_at);
  });
}

/* Who last moved this incident, and under which verb — the attribution a review asks for.
 *
 * A function rather than two fields read inline, because the row shows *one* name and choosing
 * which is a judgement: a resolved incident that was acknowledged first has both, and the
 * resolution is the newer fact. Reading `acknowledged_by` there would name the person who picked
 * it up as the person who closed it, which is exactly the misattribution the two columns exist to
 * prevent.
 *
 * `null` for an open incident, which has no actor by construction — a monitor opened it, and
 * inventing "the system" as a name would put a fiction where a review expects a person.
 */
export interface Attribution {
  readonly verb: 'acknowledged' | 'resolved';
  /* `null` means the state was set before the platform recorded who acted. Deliberately not
   * collapsed into "unknown" here — the caller decides how to say that, and the model's job is only
   * to be honest that there is no name. */
  readonly who: string | null;
}

export function attributionOf(incident: Incident): Attribution | null {
  if (incident.state === 'resolved')
    return { verb: 'resolved', who: incident.resolved_by };
  if (incident.state === 'acknowledged')
    return { verb: 'acknowledged', who: incident.acknowledged_by };
  return null;
}

/* The i18n key for a state's label. A function rather than a template literal at the call site, so
 * `copy.test.ts` can enumerate the keys this feature needs and fail on a missing translation
 * instead of rendering the key itself into the page. */
export function stateLabelKey(state: IncidentState): string {
  return `incidents.state.${state}.label`;
}

export function stateMeaningKey(state: IncidentState): string {
  return `incidents.state.${state}.meaning`;
}

/* How long the condition has held, in whole units, for a reader rather than for arithmetic.
 *
 * Duration is the fact an incident exists to record, and a raw timestamp makes every reader compute
 * it. Returns `null` rather than "0 minutes" for a clock skew that puts the onset in the future:
 * "less than a minute ago" would be a claim, and this has none to make.
 */
export function ageInMinutes(incident: Incident, now: Date): number | null {
  const opened = Date.parse(incident.opened_at);
  if (Number.isNaN(opened)) return null;
  const minutes = Math.floor((now.getTime() - opened) / 60_000);
  return minutes < 0 ? null : minutes;
}

/* ---------------------------------------------------------------- grouping for the queue

 * The queue as severity groups rather than one flat list.
 *
 * **Ordering already ranks by severity; grouping makes that ranking legible.** `orderForQueue` puts
 * the worst first, and in a flat list a reader has to notice where one severity stops and the next
 * begins — which nothing on the row tells them. A heading per severity means somebody can stop
 * reading at the end of the first group and know they have seen the worst.
 *
 * Empty severities are **omitted**, not rendered as empty groups: "no critical incidents" is worth
 * saying once for the whole screen, which the empty state already does, and a row of empty headings
 * makes a healthy estate look like a form nobody filled in.
 */
export interface IncidentGroup {
  readonly severity: Severity;
  readonly incidents: readonly Incident[];
}

export function groupForQueue(incidents: readonly Incident[]): readonly IncidentGroup[] {
  const ordered = orderForQueue(incidents);
  const groups: IncidentGroup[] = [];
  for (const incident of ordered) {
    const last = groups.at(-1);
    /* The input is already severity-ordered, so a run of one severity is contiguous and this needs
     * no map: appending to the open group is enough, and it keeps `orderForQueue`'s tiebreaker
     * (oldest first within a severity) rather than re-sorting and losing it. */
    if (last?.severity === incident.severity) {
      groups[groups.length - 1] = {
        severity: last.severity,
        incidents: [...last.incidents, incident],
      };
    } else {
      groups.push({ severity: incident.severity, incidents: [incident] });
    }
  }
  return groups;
}

/* ---------------------------------------------------------------- the queue's two filters

 * **Which incidents a reader is looking at: the design's four pills.**
 *
 * `active` is the queue's own default and the platform's own word — `Incident.active` is a server
 * field, so open ∪ acknowledged is the server's definition rather than this screen's. The other three
 * are the states themselves, because a resolved incident is *kept*: an incident review reads last
 * quarter's closed ones, and a screen that could only show active ones would make the record
 * unreachable through the product. `all` stays a value the reads understand and is not a pill — the
 * design draws four, and Active beside Resolved already covers every incident.
 */
export type IncidentFilter = 'active' | 'open' | 'acknowledged' | 'resolved' | 'all';

export const INCIDENT_FILTERS: readonly IncidentFilter[] = [
  'active',
  'open',
  'acknowledged',
  'resolved',
];

/** The `state` query parameter for a filter, or `null` where the filter is not one state.
 *
 * `active` and `all` both answer `null` and mean different things — `active` is served by the queue
 * route, which is active-only by construction, and `all` by the paged route with no state filter. So
 * a caller must not treat a `null` here as "no filtering needed"; `usesQueueRoute` is what
 * distinguishes them.
 */
export function stateParamFor(
  filter: IncidentFilter,
): 'open' | 'acknowledged' | 'resolved' | null {
  return filter === 'active' || filter === 'all' ? null : filter;
}

/** Whether this filter is served by `/incidents` rather than `/incidents/all`.
 *
 * True only for `active`, and it is the one filter the paged route cannot express: `state` there is a
 * single equality, so "open or acknowledged" would be two requests whose truncation is two separate
 * caps. The queue route answers it in one, severity-ordered, which is what this screen wants anyway.
 */
export function usesQueueRoute(filter: IncidentFilter): boolean {
  return filter === 'active';
}

/* ---------------------------------------------------------------- naming and duration

 * A short, quotable form of the id.
 *
 * The design's incidents are named `INC-2291`; this platform's are UUIDs, and a reader who has to
 * quote one in a message needs something shorter than 36 characters. The full id stays on the detail
 * page and in the URL, so nothing is hidden — this is a label, not an identifier.
 */
export function shortIncidentId(incidentId: string): string {
  return `inc-${incidentId.slice(0, 8)}`;
}

/** How long the condition has held, as a copy key and a count, or `null` when there is no claim.
 *
 * The thresholds live here rather than in the component because two surfaces read them — the card's
 * "Held 14 minutes" and the detail page's "Held for" fact — and a duration that rounded differently
 * in two places on one screen would look like a bug in the clock.
 */
export interface AgeReading {
  readonly key: string;
  readonly count: number;
}

export function ageReading(incident: Incident, now: Date): AgeReading | null {
  const minutes = ageInMinutes(incident, now);
  if (minutes === null) return null;
  if (minutes < 60) return { key: 'incidents.age.minutes', count: minutes };
  if (minutes < 60 * 24) {
    return { key: 'incidents.age.hours', count: Math.floor(minutes / 60) };
  }
  return { key: 'incidents.age.days', count: Math.floor(minutes / (60 * 24)) };
}

/* ---------------------------------------------------------------- the timeline

 * What happened to this incident, in order, from the incident's own columns.
 *
 * **There is no `acknowledged_at`.** Not on the wire, not in `_incident_json`, and not in the table —
 * migration 0032 added `acknowledged_by` and `resolved_by` and no timestamps. So an acknowledgement
 * is an event with a name and no time, and this returns `at: null` for it rather than reaching for
 * `opened_at` or `resolved_at` to fill the column. A borrowed timestamp on an audit surface is worse
 * than a blank one: it would read as the moment somebody picked it up.
 *
 * Only events that actually happened appear. The design's own timeline also carries `Notified` and
 * `Recurred` rows; this platform records neither per incident — delivery attempts are not exposed by
 * any route, and a recurrence is a *separate* incident with no link back — so inventing either would
 * be putting a fiction on the one panel a review reads.
 */
export type TimelineVerb = 'opened' | 'acknowledged' | 'resolved';

export interface TimelineEntry {
  readonly verb: TimelineVerb;
  /** `null` for an acknowledgement: the platform stores no time for it. */
  readonly at: string | null;
  /** `null` when a monitor did it, or when the actor predates the attribution columns. */
  readonly who: string | null;
}

export function timelineFor(incident: Incident): readonly TimelineEntry[] {
  const entries: TimelineEntry[] = [
    /* Opened has no actor by construction — a monitor opened it, and naming "the system" would put
     * a fiction where a review expects a person. */
    { verb: 'opened', at: incident.opened_at, who: null },
  ];

  /* An incident that was acknowledged and then resolved keeps both rows: the handover is what an
   * incident review asks about. Read from `acknowledged_by` rather than from the state, so a
   * resolved incident that was acknowledged first still shows the pick-up. */
  if (incident.acknowledged_by !== null || incident.state === 'acknowledged') {
    entries.push({ verb: 'acknowledged', at: null, who: incident.acknowledged_by });
  }
  if (incident.state === 'resolved') {
    entries.push({
      verb: 'resolved',
      at: incident.resolved_at,
      who: incident.resolved_by,
    });
  }
  return entries;
}

/* ---------------------------------------------------------------- refusals

 * Why an action is not offered, in the platform's own terms.
 *
 * The design renders a blocked action as a dead chip carrying its reason, and that is better than
 * hiding it: a reader who expects an Acknowledge button and finds nothing cannot tell a missing
 * feature from a state machine. Both reasons name what the server actually refuses.
 */
export function blockedReasonKey(
  incident: Incident,
  action: 'acknowledge' | 'resolve',
): string | null {
  if (action === 'acknowledge') {
    if (canAcknowledge(incident)) return null;
    return incident.state === 'acknowledged'
      ? 'incidents.blocked.alreadyAcknowledged'
      : 'incidents.blocked.resolvedCannotAcknowledge';
  }
  return canResolve(incident) ? null : 'incidents.blocked.alreadyResolved';
}

/* ------------------------------------------------------------------ notification delivery

 * What the incident's delivery attempts mean, decided here rather than in the panel.
 *
 * **The judgement this file exists to make is that an empty list is not a failure.** The platform
 * default routes critical incidents only, so a `warning` incident on a tenant who has declared no
 * rules is recorded and deliberately not sent — and no attempt is logged for it, because a row per
 * non-notified incident would bury the failed ones the log exists to surface. A panel reading
 * `attempts.length === 0` as "delivery failed" would report a policy decision as a fault, on the
 * one surface whose purpose is telling those apart.
 */

/** What the panel says about one incident's delivery, in one word.
 *
 * Four states and not three: `nothing_attempted` and `failed` are the pair that must never collapse,
 * and `partial` exists because a tenant may route one incident to a mailbox *and* a webhook — so
 * "delivered" as a single boolean would report a delivered email while a failing webhook went
 * unnoticed. `AlertService.dispatch` returns one outcome per destination for the same reason.
 */
export type DeliveryVerdict = 'delivered' | 'partial' | 'failed' | 'nothing_attempted';

export interface DeliveryReading {
  readonly verdict: DeliveryVerdict;
  readonly delivered: number;
  readonly failed: number;
}

export function deliveryReading(
  attempts: readonly NotificationAttempt[],
): DeliveryReading {
  const delivered = attempts.filter((attempt) => attempt.succeeded).length;
  const failed = attempts.length - delivered;
  if (attempts.length === 0)
    return { verdict: 'nothing_attempted', delivered: 0, failed: 0 };
  if (failed === 0) return { verdict: 'delivered', delivered, failed };
  if (delivered === 0) return { verdict: 'failed', delivered, failed };
  return { verdict: 'partial', delivered, failed };
}

/** The tone for a verdict. A total map, so a fifth verdict is a compile error.
 *
 * `satisfies Record<DeliveryVerdict, …>` rather than a `switch` with a `default:`, which is the
 * defect this codebase has shipped twice — in `verdictTone` on the evals screen and again on the
 * anomaly screen, both times rendering an unknown verdict in the *permitted* tone and stripping a
 * failure of its colour.
 *
 * `nothing_attempted` is `unknown` and never `ok`: it is the hue reserved for "we cannot say", and
 * painting it green would tell a reader an alert went out when the platform never tried.
 */
export const DELIVERY_TONES = {
  delivered: 'ok',
  partial: 'warn',
  failed: 'danger',
  nothing_attempted: 'unknown',
} as const satisfies Record<DeliveryVerdict, BadgeTone>;

/* `BadgeTone` and not a local union, which the first draft of this had — `'blocked'` is the
 * marketplace screen's word and `StatusBadge` has never had it, so a second vocabulary here would
 * have needed a mapping between them. Borrowing the kit's type means a seventh tone is a compile
 * error in one place rather than a badge rendering in the fallback colour. */
export function deliveryTone(verdict: DeliveryVerdict): BadgeTone {
  return DELIVERY_TONES[verdict];
}

/** Whether this incident's severity is one the platform notifies about by default.
 *
 * **The answer to "why was I not told", and the reason the panel can give it without a second
 * request.** Most monitor breaches are `warning` — `evaluate_freshness` reserves critical for three
 * times the threshold, on the reasoning that a feed an hour late is probably a late batch — and
 * `RoutingPolicy.default_for` is critical-only. So an undeclared tenant sees a queue full of
 * warnings and an empty delivery panel, which is correct and needs explaining.
 *
 * It is a *hint* rather than a verdict, and the panel words it that way: this screen does not read
 * the tenant's routing rules, so it cannot know whether they declared a warning-level rule. What it
 * can say is which explanation to look at first.
 */
export function isDefaultNotifiedSeverity(severity: Severity): boolean {
  return severity === 'critical';
}
